import { useState, useCallback, useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Plus } from "lucide-react";
import { SiteEyebrow } from "@/components/sections/workshop-ticket-block";
import { BlurFade } from "@/components/ui/blur-fade";
import { requestCheckout } from "@/lib/open-checkout";
/* One definition of the house arrow, shared with the reviews and OnPrompt
   carousels. This file used to hold it inline; see ui/drawn-arrow.tsx. */
import { DrawnArrow } from "@/components/ui/drawn-arrow";

/**
 * "Is this workshop for you?" — persona carousel for the Claude Cowork page.
 * Eight roles as a horizontal embla carousel on desktop (drag + arrows, starts
 * offset, bleeds off the right). On mobile (<768px) embla is deactivated and the
 * cards STACK vertically (shorter, full-width) — no slider.
 *
 * Disclosure is CLICK-only: the "+" toggles a card's white build panel.
 * Personas + builds are grounded in the Cowork transcripts.
 */

export interface Persona {
  key: string;
  /** Card title — may contain \n for an intentional two-line break
   *  (rendered with white-space: pre-line). */
  title: string;
  build: string;
  bg: string;
  /** Optional title color for light card backgrounds; defaults to cream. */
  fg?: string;
  img: string;
  alt: string;
}

export const COWORK_PERSONAS: Persona[] = [
  {
    key: "founders",
    title: "Founders",
    build:
      "Wire Claude into your calendar and inbox, then have it brief your whole week every Monday before you're up. Save the workflow as a one-command skill your team can reuse.",
    bg: "#D94000",
    img: "/images/persona-cards/founders.webp",
    alt: "A smiling founder with the clay Claude robot, which wears a blazer, on his shoulder.",
  },
  {
    key: "executives",
    title: "Executives\n& VPs",
    build:
      "Turn a messy board deck into a clean one-page brief, then build the SOPs and shared skills your whole org runs on, connected to Drive, Slack, and Notion.",
    // Lightened gold + charcoal title — cream text on the old #E0A317 was
    // too low-contrast.
    bg: "#F2C14E",
    fg: "var(--charcoal)",
    img: "/images/persona-cards/executives.webp",
    alt: "An executive beside the clay Claude robot, which carries a briefcase.",
  },
  {
    key: "marketing",
    title: "Marketing",
    build:
      "Build a live dashboard from your messy ad data, then deploy an agent that refreshes your social posts and graphics on a schedule while you sleep.",
    bg: "#6D3FC4",
    img: "/images/persona-cards/marketers.webp",
    alt: "A marketer high-fiving the clay Claude robot, which wears headphones and a cap.",
  },
  {
    key: "sales",
    title: "Sales",
    build:
      "Connect Claude to your CRM, then have it prep every call overnight with battle cards and fresh account research. Turn the routine into a skill the whole team reuses.",
    bg: "#358066",
    img: "/images/persona-cards/sales.webp",
    alt: "A salesperson with the clay Claude robot on their shoulder, wearing a headset.",
  },
  {
    key: "designers",
    title: "Designers",
    build:
      "Turn a one-line direction into a polished, on-brand deck, then save your style as a reusable skill that pulls the right logos, fonts, and colors every time.",
    bg: "#C0397B",
    img: "/images/persona-cards/designers.webp",
    alt: "A designer beside the clay Claude robot, which wears a beret and holds an easel.",
  },
  {
    key: "finance-ops",
    title: "Finance & ops",
    build:
      "Build a P&L reconciliation that flags the discrepancy you would miss, then schedule it to run every month, wired straight into your books and spreadsheets.",
    bg: "#15706B",
    img: "/images/persona-cards/finance-ops.webp",
    alt: "A finance lead with the clay Claude robot, which examines a paper through a magnifier.",
  },
  {
    key: "consultants",
    title: "Consultants",
    build:
      "Turn eight messy sources into a cited research brief, then deploy a research agent that profiles a company and its rivals and grades its own work before you read it.",
    bg: "#3B2E8F",
    img: "/images/persona-cards/consultants.webp",
    alt: "A consultant holding the clay Claude robot, which wears glasses and holds a pen.",
  },
  {
    key: "product",
    title: "Product managers",
    build:
      "Turn scattered user feedback into a ranked roadmap, then connect Claude to your tools so it pulls tickets, calls, and surveys automatically every sprint.",
    bg: "#2E2E2C",
    img: "/images/persona-cards/product.webp",
    alt: "A product manager beside the clay Claude robot, which holds a clipboard.",
  },
];

const CARD_RADIUS = 12;

export function PersonaCarousel({
  eyebrow,
  heading,
  subheading,
  className = "",
  personas = COWORK_PERSONAS,
  autoOpenFirstOnMobile = true,
  revealOnHover = false,
  showPlus = false,
  floatIn = false,
}: {
  className?: string;
  personas?: Persona[];
  /** Overrides the default "Is this workshop for you?" header. Optional, so
   *  the Cowork, Design and Code pages keep the copy they ship with. */
  eyebrow?: string;
  heading?: ReactNode;
  /** Optional right-column line beside the headline. Omitted, nothing renders. */
  subheading?: ReactNode;
  /* Mobile opens the Founders card post-mount so the build-panel pattern is
     self-evident. The Claude Code page opts out — there the + button is the
     affordance and one card hanging open read as a layout inconsistency. */
  autoOpenFirstOnMobile?: boolean;
  /* Claude for Beginners: no + button. The build text rises over a gradient on
     hover or keyboard focus, and a tap toggles it on touch screens. The panel
     stays readable by assistive tech the whole time. Off everywhere else. */
  revealOnHover?: boolean;
  /** With revealOnHover: still show the + button as the visible cue, and let
   *  it toggle the same panel (Claude for Beginners, Esther 2026-11-03). */
  showPlus?: boolean;
  /** The cards rise into place one after another, slightly staggered, once
   *  the row is a quarter on screen (AI Video page, Esther 2026-10-28). Uses
   *  the `translate` property so it never fights the cards' own transforms.
   *  Cards stay visible until the row is near, and reduced motion skips it. */
  floatIn?: boolean;
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true,
    breakpoints: {
      // Mobile: deactivate the carousel so the cards stack in normal flow.
      "(max-width: 767px)": { active: false },
    },
  });

  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!emblaApi) return;
    const update = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    update();
    emblaApi.on("select", update).on("reInit", update).on("resize", update);
    return () => {
      emblaApi.off("select", update).off("reInit", update).off("resize", update);
    };
  }, [emblaApi]);

  const trackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = trackRef.current;
    if (!floatIn || !el || !("IntersectionObserver" in window)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Arm only rows still below the fold, so a card is never hidden on screen.
    if (el.getBoundingClientRect().top < window.innerHeight) return;
    el.dataset.float = "armed";
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.dataset.float = "in";
        io.disconnect();
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [floatIn]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const toggle = useCallback((key: string) => {
    setOpenKey((prev) => (prev === key ? null : key));
  }, []);

  // Mobile (<768px, stacked layout): the Founders card starts open so the
  // build-panel pattern is self-evident; every other card is click-only via
  // the + button. Set post-mount rather than in initial state so SSR markup —
  // which can't know the viewport — hydrates cleanly. Desktop (>=768px) keeps
  // click-only behavior with all cards closed.
  useEffect(() => {
    if (!autoOpenFirstOnMobile) return;
    if (window.matchMedia("(max-width: 767px)").matches) {
      setOpenKey("founders");
    }
  }, [autoOpenFirstOnMobile]);

  const arrowBtn = (dir: "prev" | "next", enabled: boolean, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      aria-label={dir === "prev" ? "Previous personas" : "Next personas"}
      className="persona-arrow"
      style={{
        background: "none",
        border: "none",
        padding: 0,
        display: "flex",
        cursor: enabled ? "pointer" : "default",
        opacity: enabled ? 0.75 : 0.15,
        transition: "opacity 200ms",
      }}
    >
      <DrawnArrow dir={dir} width={74} />
    </button>
  );

  return (
    <section ref={sectionRef} className={className} style={{ backgroundColor: "var(--pc-ground, var(--cream))" }}>
      {/* Header: left headline (aligned to container/logo), right subheadline */}
      <div className="container-content" style={{ paddingBottom: "clamp(56px, 7vw, 104px)" }}>
        <BlurFade inView delay={0}>
          <div className="persona-header">
            {/* Eyebrow and headline share ONE grid cell. `.persona-header` is a
                two-column grid at desktop (1.3fr / 1fr, index.css), and it used
                to have a single child, so the h2 filled the left column. Adding
                the eyebrow as a sibling made it child one and pushed the
                headline into the right-hand column. */}
            <div>
              {eyebrow && <SiteEyebrow label={eyebrow} />}
              <h2
                className="type-h2"
                style={{ color: "var(--pc-heading, var(--charcoal))", maxWidth: "var(--max-w-heading)" }}
              >
                {heading ?? (
                  <>
                    Is this workshop for you?{" "}
                    <span className="pc-yeah" style={{ fontStyle: "italic", color: "var(--terra)" }}>Yeah, probably</span>
                    &hellip; if you use a computer, like, ever.
                  </>
                )}
              </h2>
            </div>
            {subheading && (
              <div className="persona-header-right">
                <p
                  className="type-body-lg"
                  style={{
                    color: "var(--pc-subheading, var(--charcoal-muted))",
                    maxWidth: "34ch",
                    textAlign: "right",
                    textWrap: "balance",
                  }}
                >
                  {subheading}
                </p>
              </div>
            )}
          </div>
        </BlurFade>
      </div>

      {/* Track — desktop: offset + right bleed + arrows. Mobile: stacked, no arrows. */}
      {floatIn && (
        <style>{`
          .persona-track[data-float] .persona-card { transition: translate 1300ms cubic-bezier(.16,1,.3,1), opacity 1000ms ease; }
          .persona-track[data-float="armed"] .persona-card { translate: 0 130px; opacity: 0; }
          .persona-track[data-float="in"] .persona-card { translate: 0 0; opacity: 1; }
          ${personas.map((_, i) => `.persona-track[data-float] .persona-card-wrap:nth-child(${i + 1}) .persona-card { transition-delay: ${i * 160}ms; }`).join("\n          ")}
        `}</style>
      )}
      <div className="persona-track" ref={trackRef}>
        <div className="persona-arrows">
          {arrowBtn("prev", canPrev, scrollPrev)}
          {arrowBtn("next", canNext, scrollNext)}
        </div>
        <div ref={emblaRef} className="persona-viewport" style={{ overflow: "hidden" }}>
          <div className="persona-cards-row">
            {personas.map((p, i) => {
              const isOpen = openKey === p.key;
              const buildId = `persona-build-${p.key}`;
              const shift = i % 2 === 1 ? "clamp(36px, 4vw, 72px)" : "0px";
              const cardStyle = {
                "--persona-shift": shift,
                /* The card's two colours as variables, so a page's stylesheet can
                   ink the + button and the reveal panel in the card's own family
                   (Vivid 2026 tone-on-tone, Code + Cowork). Unused elsewhere. */
                "--pc-bg": p.bg,
                "--pc-fg": p.fg ?? "var(--cream)",
                position: "relative",
                backgroundColor: p.bg,
                borderRadius: CARD_RADIUS,
                overflow: "hidden",
              } as CSSProperties;
              return (
                <BlurFade key={p.key} inView delay={0.05 + i * 0.04} className="persona-card-wrap">
                  <article
                    className="persona-card persona-card-shift group"
                    data-open={isOpen ? "true" : undefined}
                    data-reveal={revealOnHover ? "hover" : undefined}
                    tabIndex={revealOnHover ? 0 : undefined}
                    onClick={
                      revealOnHover
                        ? () => {
                            // With showPlus, a phone opens the card only from the + button.
                            if (showPlus && window.matchMedia("(max-width: 767px)").matches) return;
                            toggle(p.key);
                          }
                        : undefined
                    }
                    style={cardStyle}
                  >
                    {visible && (
                      <img
                        src={p.img}
                        alt={p.alt}
                        width={340}
                        height={453}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "top center",
                        }}
                      />
                    )}

                    {/* Persona title — serif, cream (or per-persona fg), top-left.
                        pre-line honors intentional \n breaks in the data. */}
                    <span
                      style={{
                        position: "absolute",
                        top: 26,
                        left: 28,
                        zIndex: 2,
                        fontFamily: "var(--font-display)",
                        fontWeight: 500,
                        fontSize: "clamp(26px, 2.1vw, 33px)",
                        lineHeight: 1.05,
                        letterSpacing: "-0.01em",
                        color: p.fg ?? "var(--cream)",
                        maxWidth: "70%",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {p.title}
                    </span>

                    {/* + button (click-only toggle) */}
                    {(!revealOnHover || showPlus) && (
                    <button
                      type="button"
                      className="persona-plus-btn"
                      onClick={(e) => {
                        // The card itself toggles in reveal mode; don't toggle twice.
                        e.stopPropagation();
                        toggle(p.key);
                      }}
                      aria-expanded={isOpen}
                      aria-controls={buildId}
                      aria-label={`Show what ${p.title.replace(/\n/g, " ").toLowerCase()} can build`}
                      style={{
                        position: "absolute",
                        bottom: 16,
                        right: 16,
                        zIndex: 4,
                        width: 46,
                        height: 46,
                        borderRadius: 999,
                        border: "none",
                        cursor: "pointer",
                        background: "var(--cream)",
                        color: "var(--charcoal)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Plus className="persona-plus" size={22} aria-hidden="true" />
                    </button>
                    )}

                    {/* Reveal: white build card — direct build promise + register link */}
                    <div
                      id={buildId}
                      className="persona-build"
                      aria-hidden={revealOnHover ? undefined : !isOpen}
                      style={{
                        position: "absolute",
                        left: 10,
                        right: 10,
                        bottom: 10,
                        zIndex: 3,
                        background: "var(--cream)",
                        color: "var(--charcoal)",
                        border: "1px solid var(--charcoal)",
                        borderRadius: CARD_RADIUS,
                        padding: "16px 18px 16px",
                      }}
                    >
                      <p className="type-body-sm" style={{ color: "var(--charcoal)", paddingRight: 40 }}>
                        {p.build}
                      </p>
                      <a
                        href="#tickets"
                        className="persona-register"
                        tabIndex={revealOnHover || isOpen ? 0 : -1}
                        data-testid={`register-${p.key}`}
                        onClick={(e) => {
                          // OPENS THE CHECKOUT (sheet on mobile, drawer on desktop); anchor-scroll runs only when nothing is bookable.
                          if (requestCheckout()) e.preventDefault();
                        }}
                      >
                        Reserve your seat
                      </a>
                    </div>
                  </article>
                </BlurFade>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
